import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type ApiError, apiFetch } from '@/lib/api/client';
import { useToast } from '@/components/ui/toast';
import type { ApiInvestmentResponse } from './types';
import { INVESTMENTS_QUERY_KEY } from './useInvestments';

/**
 * Reativa um investimento desinvestido: POST /api/investments/{id}/reactivate com If-Match.
 * Comentário obrigatório. O backend limpa data de saída + métricas realizadas (histórico preserva).
 * Em sucesso, invalida ficha e lista. 412 → aviso + refetch.
 */
export function useReactivateInvestment() {
  const qc = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: ({ id, etag, comment }: { id: string; etag: string; comment: string }) =>
      apiFetch<ApiInvestmentResponse>(`/api/investments/${id}/reactivate`, {
        method: 'POST',
        headers: { 'If-Match': `"${etag}"` },
        body: { comment: comment.trim() },
      }),
    onSuccess: (resp) => {
      void qc.invalidateQueries({ queryKey: ['investment', resp.id] });
      void qc.invalidateQueries({ queryKey: INVESTMENTS_QUERY_KEY });
      toast(`Investimento reativado: ${resp.codename}.`);
    },
    onError: (err, vars) => {
      if ((err as ApiError).status === 412) {
        toast('Esse investimento foi alterado por outra pessoa. Atualizei a ficha.', 'warn');
        void qc.invalidateQueries({ queryKey: ['investment', vars.id] });
      } else {
        toast('Não foi possível reativar o investimento. Tente de novo.', 'danger');
      }
    },
  });
}
